// ============================================================================
// Property detail tabs: permission-gated for Owner vs Team (module permissions)
// Slice 22: Team sees only granted modules; each grant = full use of that tab.
// Owner-only forever: Payments config, Archive, billing (not grantable).
// ============================================================================

import {
  hasTeamPermission,
  isAllPermissionsSelected,
  resolveTeamPermissions,
  tabAllowedByPermissions,
  type TeamPermissionModule,
} from "@/lib/teamPermissions";

export type PropertyTabId =
  | "overview"
  | "schedule"
  | "my-tasks"
  | "checklist"
  | "inventory"
  | "unit"
  | "rooms"
  | "access"
  | "payments"
  | "billing"   // legacy: redirects to payments
  | "invoices"  // legacy: redirects to payments
  | "reports"
  | "maintenance"
  | "performance"
  | "automations"
  | "settings";

export interface PropertyTabConfig {
  value: PropertyTabId;
  label: string;
}

export type PropertyAccessLevel = "full" | "limited";
export type PropertyUserRole = "owner" | "cohost";

const ALL_TABS: PropertyTabConfig[] = [
  { value: "overview", label: "Overview" },
  { value: "access", label: "Access" },
  { value: "schedule", label: "Schedule" },
  { value: "my-tasks", label: "My Tasks" },
  { value: "checklist", label: "Checklist" },
  { value: "inventory", label: "Inventory" },
  { value: "unit", label: "Unit" },
  { value: "rooms", label: "Rooms" },
  { value: "maintenance", label: "Issues" },
  { value: "payments", label: "Payments" },
  { value: "reports", label: "Reports" },
  { value: "performance", label: "Performance" },
  { value: "automations", label: "Automations" },
  { value: "settings", label: "Settings" },
];

/**
 * Slice 10 — MVP chrome: only these tabs show in nav.
 * Hidden (code kept): Checklist · Payments · Performance · Automations
 * Slice 20: Unit baseline tab added.
 * Slice 30: Rooms tab is the film-a-room onboard home.
 * Slice 35: Access (keys & entry) tab.
 * My Tasks is inserted after Schedule when the user can execute assigned work.
 */
export const MVP_NAV_TAB_IDS: PropertyTabId[] = [
  "overview",
  "access",
  "schedule",
  "inventory",
  "unit",
  "rooms",
  "maintenance",
  "reports",
  "settings",
];

/**
 * Hidden from MVP sidebar but still reachable via URL / in-app CTAs
 * (e.g. Inventory leave-out rules still deep-link checklist for advanced editors).
 */
export const DEEP_LINK_TAB_IDS: PropertyTabId[] = [
  "checklist",
  "payments",
  "performance",
  "automations",
];

const MVP_NAV_TABS: PropertyTabConfig[] = MVP_NAV_TAB_IDS.map(
  (id) => ALL_TABS.find((t) => t.value === id)!,
);

const MY_TASKS_TAB = ALL_TABS.find((t) => t.value === "my-tasks")!;

function withMyTasksTab(
  tabs: PropertyTabConfig[],
  include: boolean
): PropertyTabConfig[] {
  if (!include) return tabs;
  if (tabs.some((t) => t.value === "my-tasks")) return tabs;
  const scheduleIdx = tabs.findIndex((t) => t.value === "schedule");
  if (scheduleIdx >= 0) {
    return [...tabs.slice(0, scheduleIdx + 1), MY_TASKS_TAB, ...tabs.slice(scheduleIdx + 1)];
  }
  return [MY_TASKS_TAB, ...tabs];
}

/**
 * Returns tabs allowed for the current user in property context (MVP nav only).
 * Hidden tabs remain in the type system / routes but are not listed.
 */
export function getAllowedTabs(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): PropertyTabConfig[] {
  if (role === "owner") {
    return withMyTasksTab(MVP_NAV_TABS, true);
  }
  if (role === "cohost") {
    const resolved = resolveTeamPermissions(permissions ?? null, accessLevel);
    const base = MVP_NAV_TABS.filter((t) => tabAllowedByPermissions(t.value, resolved));
    return withMyTasksTab(base, hasTeamPermission(resolved, "tasks"));
  }
  return [];
}

export function isTabAllowed(
  tabId: PropertyTabId,
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  const allowed = getAllowedTabs(role, accessLevel, permissions);
  return allowed.some((t) => t.value === tabId);
}

export function isDeepLinkTab(tabId: string): tabId is PropertyTabId {
  return (DEEP_LINK_TAB_IDS as string[]).includes(tabId);
}

/**
 * Whether a tab may stay open (nav selection or deep link).
 * Checklist remains deep-linkable when Inventory is allowed.
 */
export function canOpenPropertyTab(
  tabId: PropertyTabId,
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (isTabAllowed(tabId, role, accessLevel, permissions)) return true;
  if (!isDeepLinkTab(tabId)) return false;
  if (role === "owner") return true;
  if (tabId === "checklist") {
    return isTabAllowed("inventory", role, accessLevel, permissions);
  }
  return false;
}

/**
 * Whether the user can view this tab (used for routing/visibility).
 */
export function canViewTab(
  tabId: PropertyTabId,
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  return isTabAllowed(tabId, role, accessLevel, permissions);
}

function cohostHas(
  role: PropertyUserRole,
  permissions: TeamPermissionModule[] | null | undefined,
  accessLevel: PropertyAccessLevel | null,
  module: TeamPermissionModule
): boolean {
  if (role !== "cohost") return false;
  const resolved = resolveTeamPermissions(permissions ?? null, accessLevel);
  return hasTeamPermission(resolved, module);
}

/** Owner only, or Team with Settings permission. Property-level config (name/address, iCal, rules). */
export function canEditPropertyConfig(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role === "owner") return true;
  return cohostHas(role, permissions, accessLevel, "settings");
}

/** Owner only, or Team with Settings permission. Access & Team invite/remove. */
export function canManageTeamAccess(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role === "owner") return true;
  return cohostHas(role, permissions, accessLevel, "settings");
}

/** Owner or Team with Scheduling permission. Task create/assign/edit. */
export function canOperateTasks(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role === "owner") return true;
  return cohostHas(role, permissions, accessLevel, "schedule");
}

/**
 * Owner, or Team with Full Access (every module).
 * Full Access is owner-like for property operations (delete tasks/reservations, etc.).
 * Account billing / archive remain owner-only elsewhere.
 */
export function hasFullAccess(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role === "owner") return true;
  if (role !== "cohost") return false;
  // access_level=full is set only for Full Access (all modules) or legacy full rows.
  if (accessLevel === "full") return true;
  return isAllPermissionsSelected(resolveTeamPermissions(permissions ?? null, accessLevel));
}

/**
 * Delete turnover/cleaning tasks from Schedule.
 * Owner or Full Access Team only. Schedule-only Team cannot delete.
 */
export function canDeleteTasks(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  return hasFullAccess(role, accessLevel, permissions);
}

/** Remove reservations from Schedule / calendar. Same gate as delete tasks. */
export function canDeleteReservations(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  return hasFullAccess(role, accessLevel, permissions);
}

/** Owner or Team with Inventory permission. */
export function canEditInventoryConfig(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role === "owner") return true;
  return cohostHas(role, permissions, accessLevel, "inventory");
}

/** Owner or Team with Access permission. Keys & entry setup. */
export function canEditAccessConfig(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role === "owner") return true;
  return cohostHas(role, permissions, accessLevel, "access");
}

// ============== Payments tab ==============

/** Owner only. Pay on Stravo, payment method, Approve & Pay. Never grantable to Team. */
export function canManagePaymentsOwnerOnly(
  role: PropertyUserRole,
  _accessLevel: PropertyAccessLevel | null,
  _permissions?: TeamPermissionModule[] | null
): boolean {
  return role === "owner";
}

/** Owner or Team with Settings (legacy full). Rates editing. */
export function canEditPaymentsRates(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role === "owner") return true;
  return cohostHas(role, permissions, accessLevel, "settings");
}

/** Team without settings: read-only payments if that tab were shown. */
export function isPaymentsReadOnly(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role !== "cohost") return false;
  return !cohostHas(role, permissions, accessLevel, "settings");
}

/** Owner or Team with Inventory. Mark delivered, notes. */
export function canPerformNonPaymentInventoryActions(
  role: PropertyUserRole,
  accessLevel: PropertyAccessLevel | null,
  permissions?: TeamPermissionModule[] | null
): boolean {
  if (role === "owner") return true;
  return cohostHas(role, permissions, accessLevel, "inventory");
}
